// src/components/partner/CreateCohortModal.jsx

import {
  useEffect,
  useState,
} from "react";

import {
  CalendarDays,
  X,
} from "lucide-react";

import {
  createPartnerCohort,
  PROGRAMME_TYPE_OPTIONS,
} from "../../lib/partner/partnerCohorts";


const INITIAL_FORM = {
  name: "",
  programmeType: "",
  startDate: "",
  endDate: "",
  capacity: "",
};


export default function CreateCohortModal({
  open,
  partnerId,
  onClose,
  onCreated,
}) {
  const [
    form,
    setForm,
  ] = useState(
    INITIAL_FORM
  );


  const [
    saving,
    setSaving,
  ] = useState(false);


  const [
    error,
    setError,
  ] = useState("");


  /* ------------------------------------------------------------------------ */
  /* Lifecycle                                                                */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!open) {
      setForm(
        INITIAL_FORM
      );

      setError("");
      setSaving(false);

      return undefined;
    }


    const handleEscape = (
      event
    ) => {
      if (
        event.key ===
          "Escape" &&
        !saving
      ) {
        onClose?.();
      }
    };


    window.addEventListener(
      "keydown",
      handleEscape
    );


    return () => {
      window.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [
    open,
    saving,
    onClose,
  ]);


  if (!open) {
    return null;
  }


  /* ------------------------------------------------------------------------ */
  /* Form                                                                     */
  /* ------------------------------------------------------------------------ */

  function updateField(
    field,
    value
  ) {
    setForm(
      (current) => ({
        ...current,

        [field]:
          value,
      })
    );
  }


  function handleBackdropClick(
    event
  ) {
    if (
      event.target ===
        event.currentTarget &&
      !saving
    ) {
      onClose?.();
    }
  }


  async function handleSubmit(
    event
  ) {
    event.preventDefault();


    try {
      setSaving(true);
      setError("");


      const cohortId =
        await createPartnerCohort(
          {
            partnerId,

            name:
              form.name,

            programmeType:
              form.programmeType,

            startDate:
              form.startDate,

            endDate:
              form.endDate,

            capacity:
              form.capacity,
          }
        );


      await onCreated?.(
        cohortId
      );

      onClose?.();
    } catch (
      submitError
    ) {
      console.error(
        "Create cohort failed:",
        submitError
      );


      setError(
        submitError?.message ||
          "The cohort could not be created."
      );
    } finally {
      setSaving(false);
    }
  }


  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <div
      role="presentation"
      onMouseDown={
        handleBackdropClick
      }
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/45 p-0 sm:items-center sm:p-6"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-cohort-title"
        className="max-h-[95vh] w-full overflow-y-auto rounded-t-[28px] bg-white shadow-2xl sm:max-w-xl sm:rounded-[28px]"
      >

        {/* Header */}

        <header className="flex items-start justify-between border-b border-[#EFEFEF] px-6 py-5">

          <div>
            <h2
              id="create-cohort-title"
              className="text-2xl font-black text-[#333333]"
            >
              Create cohort
            </h2>

            <p className="mt-1 max-w-md text-sm leading-relaxed text-[#757575]">
              Set up a programme group
              so you can track
              participation and
              progress.
            </p>
          </div>


          <button
            type="button"
            onClick={
              onClose
            }
            disabled={
              saving
            }
            aria-label="Close create cohort form"
            className="flex h-10 w-10 items-center justify-center rounded-full text-[#757575] transition hover:bg-[#FFF4EC] hover:text-[#FF914D] disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>

        </header>


        <form
          onSubmit={
            handleSubmit
          }
          className="space-y-5 p-6"
        >

          {error && (
            <div
              role="alert"
              className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
            >
              {error}
            </div>
          )}


          {/* Cohort name */}

          <div>
            <label
              htmlFor="cohort-name"
              className="block text-sm font-black text-[#333333]"
            >
              Cohort name
            </label>

            <input
              id="cohort-name"
              value={
                form.name
              }
              onChange={(
                event
              ) =>
                updateField(
                  "name",
                  event.target
                    .value
                )
              }
              disabled={
                saving
              }
              required
              maxLength={
                120
              }
              placeholder="For example: August 2026 Intake"
              className="mt-2 w-full rounded-2xl border border-[#DDDDDD] px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
            />
          </div>


          {/* Programme type */}

          <div>
            <label
              htmlFor="cohort-programme-type"
              className="block text-sm font-black text-[#333333]"
            >
              Programme type
            </label>

            <select
              id="cohort-programme-type"
              value={
                form.programmeType
              }
              onChange={(
                event
              ) =>
                updateField(
                  "programmeType",
                  event.target
                    .value
                )
              }
              disabled={
                saving
              }
              required
              className="mt-2 w-full rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
            >
              <option
                value=""
                disabled
              >
                Select programme type
              </option>

              {PROGRAMME_TYPE_OPTIONS.map(
                (option) => (
                  <option
                    key={
                      option.value
                    }
                    value={
                      option.value
                    }
                  >
                    {option.label}
                  </option>
                )
              )}
            </select>
          </div>


          {/* Cohort size */}

          <div>
            <label
              htmlFor="cohort-capacity"
              className="block text-sm font-black text-[#333333]"
            >
              Cohort size
            </label>

            <input
              id="cohort-capacity"
              type="number"
              min="1"
              step="1"
              value={
                form.capacity
              }
              onChange={(
                event
              ) =>
                updateField(
                  "capacity",
                  event.target
                    .value
                )
              }
              disabled={
                saving
              }
              placeholder="For example: 30"
              className="mt-2 w-full rounded-2xl border border-[#DDDDDD] px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
            />
          </div>


          {/* Dates */}

          <div className="grid gap-4 sm:grid-cols-2">

            <div>
              <label
                htmlFor="cohort-start-date"
                className="block text-sm font-black text-[#333333]"
              >
                Start date
              </label>

              <div className="relative mt-2">
                <CalendarDays className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#999999]" />

                <input
                  id="cohort-start-date"
                  type="date"
                  value={
                    form.startDate
                  }
                  onChange={(
                    event
                  ) =>
                    updateField(
                      "startDate",
                      event.target
                        .value
                    )
                  }
                  disabled={
                    saving
                  }
                  className="w-full rounded-2xl border border-[#DDDDDD] py-3 pl-11 pr-4 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
                />
              </div>
            </div>


            <div>
              <label
                htmlFor="cohort-end-date"
                className="block text-sm font-black text-[#333333]"
              >
                End date
              </label>

              <div className="relative mt-2">
                <CalendarDays className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#999999]" />

                <input
                  id="cohort-end-date"
                  type="date"
                  value={
                    form.endDate
                  }
                  min={
                    form.startDate ||
                    undefined
                  }
                  onChange={(
                    event
                  ) =>
                    updateField(
                      "endDate",
                      event.target
                        .value
                    )
                  }
                  disabled={
                    saving
                  }
                  className="w-full rounded-2xl border border-[#DDDDDD] py-3 pl-11 pr-4 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
                />
              </div>
            </div>

          </div>


          {/* Actions */}

          <div className="flex flex-col-reverse gap-3 border-t border-[#EFEFEF] pt-5 sm:flex-row sm:justify-end">

            <button
              type="button"
              onClick={
                onClose
              }
              disabled={
                saving
              }
              className="rounded-full border border-[#DDDDDD] px-5 py-3 text-sm font-black text-[#555555] transition hover:bg-[#F7F7F7] disabled:opacity-50"
            >
              Cancel
            </button>


            <button
              type="submit"
              disabled={
                saving
              }
              className="rounded-full bg-[#FF914D] px-6 py-3 text-sm font-black text-white transition hover:bg-[#E9782F] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving
                ? "Creating cohort…"
                : "Create cohort"}
            </button>

          </div>

        </form>
      </div>
    </div>
  );
}